import { useCallback, useEffect, useState } from "react";
import { EngagementService } from "@services/engagements/engagementService";
import type {
  CampaignPublishControl as Campaign,
  HistoryEntry,
} from "@services/engagements/commands/cmdPublishControl";
import "./campaignPublishControl.scss";

/*
    Publish used to be all-or-nothing per engagement (MPN-1364). Ops routinely stages several
    campaigns under one engagement that are not ready together — approvals outstanding, an
    asset missing, or a deliberately later start — so each campaign now carries its own gate
    and the page publishes a selection.

    This deliberately does NOT live inside the network view. That one dispatches a job and
    polls every network's API before it renders anything; readiness and history are local
    facts, and a toggle should not wait on Meta's rate limiter.
*/

const service = new EngagementService();

function formatDate(iso: string | null): string {
  if (!iso) return "Not published";
  return new Date(iso).toLocaleDateString(undefined, {
    day: "numeric", month: "short", year: "numeric",
  });
}

/*
    A date-only value ("2026-06-25"). Parsed by hand rather than through `new Date()`:
    that treats a bare date string as UTC midnight, which renders as the day BEFORE for
    anyone west of Greenwich. This is the billing boundary, so being a day out is not
    cosmetic.
*/
function formatDay(day: string | null): string {
  if (!day) return "Not live yet";
  const [year, month, date] = day.split("-").map(Number);
  return new Date(year, month - 1, date).toLocaleDateString(undefined, {
    day: "numeric", month: "short", year: "numeric",
  });
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit",
  });
}

export interface CampaignPublishControlProps {
  engagementId: number;
  /** Lifted so the Publish button can act on it — the selection IS the publish scope. */
  selectedLaunchIds: number[];
  onSelectionChange: (ids: number[]) => void;
  /** Bumped by the parent after a publish, so dates and history counts catch up. */
  refreshKey?: number;
}

export const CampaignPublishControl = ({
  engagementId, selectedLaunchIds, onSelectionChange, refreshKey = 0,
}: CampaignPublishControlProps) => {
  const [campaigns, setCampaigns] = useState<Campaign[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyLaunchId, setBusyLaunchId] = useState<number | null>(null);
  const [historyFor, setHistoryFor] = useState<number | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const fresh = await service.getPublishControl(engagementId);
      setCampaigns(fresh);
      // Prune anything that is no longer selectable. A campaign someone else turned off, or
      // that has since been deleted, would otherwise keep inflating "Publish selected (N)"
      // and promise something the server is going to refuse.
      const selectable = new Set(
        fresh.filter((c) => c.is_ready_to_publish).map((c) => c.launch_id)
      );
      const pruned = selectedLaunchIds.filter((id) => selectable.has(id));
      if (pruned.length !== selectedLaunchIds.length) onSelectionChange(pruned);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load campaigns");
    }
    // selectedLaunchIds is deliberately not a dependency: this reconciles whatever the
    // selection happens to be at fetch time, and depending on it would refetch on every click.
     
  }, [engagementId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const toggleReady = async (campaign: Campaign) => {
    setBusyLaunchId(campaign.launch_id);
    setError(null);
    try {
      const updated = await service.setLaunchReady(
        campaign.launch_id, !campaign.is_ready_to_publish
      );
      setCampaigns((prev) =>
        (prev ?? []).map((c) => (c.launch_id === updated.launch_id ? updated : c))
      );
      // Turning a campaign off must drop it from the selection, or "Publish selected" would
      // still count something the gate now refuses to publish.
      if (!updated.is_ready_to_publish) {
        onSelectionChange(selectedLaunchIds.filter((id) => id !== updated.launch_id));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not update");
    } finally {
      setBusyLaunchId(null);
    }
  };

  const toggleSelected = (launchId: number) => {
    onSelectionChange(
      selectedLaunchIds.includes(launchId)
        ? selectedLaunchIds.filter((id) => id !== launchId)
        : [...selectedLaunchIds, launchId]
    );
  };

  if (error && !campaigns) {
    return <div className="cpc__error">{error}</div>;
  }
  if (!campaigns) {
    return <div className="cpc__loading">Loading campaigns…</div>;
  }
  if (campaigns.length === 0) {
    return <div className="cpc__empty">No campaigns on this engagement yet.</div>;
  }

  return (
    <div className="cpc">
      {error && <div className="cpc__error">{error}</div>}

      <div className="cpc__head">
        <span />
        <span>Campaign</span>
        <span className="cpc__right">Went live</span>
        <span className="cpc__right">Last published</span>
        <span className="cpc__right">Ready to publish</span>
      </div>

      {campaigns.map((campaign) => {
        const selected = selectedLaunchIds.includes(campaign.launch_id);
        return (
          <div className="cpc__row" key={campaign.launch_id}>
            <div className="cpc__check">
              <input
                type="checkbox"
                checked={selected}
                // A campaign that is not ready cannot be published, so it cannot be
                // selected either — the button would otherwise promise something the
                // server is going to refuse.
                disabled={!campaign.is_ready_to_publish}
                onChange={() => toggleSelected(campaign.launch_id)}
                aria-label={`Select ${campaign.launch_name}`}
              />
            </div>

            <div className="cpc__name">
              <span className="cpc__tag">CAMPAIGN</span>
              <span className="cpc__title">{campaign.launch_name}</span>
              <span className="cpc__sub">
                {campaign.ad_group_count} {campaign.ad_group_count === 1 ? "ad group" : "ad groups"}
              </span>
              <span className={`cpc__reason cpc__reason--${campaign.status}`}>
                <i className="bi bi-record-circle" />
                {campaign.status_reason}
              </span>
            </div>

            {/*
                The billing boundary, not a schedule date: conversions before it are
                pre-converts and conversions on or after it are billable. Read-only by
                design — the title says so, because the first thing Ops asks about a date
                on a page full of toggles is how to change it.
            */}
            <div
              className={`cpc__golive${campaign.go_live_date ? "" : " cpc__golive--none"}`}
              title="Set automatically by the first publish that went live on a network. Conversions dated before it are pre-converts; on or after it they are billable. Not editable."
            >
              {formatDay(campaign.go_live_date)}
            </div>

            <div className="cpc__published">{formatDate(campaign.last_published)}</div>

            <div className="cpc__ready">
              <label className="cpc__switch">
                <span className="cpc__switch-label">
                  {campaign.is_ready_to_publish ? "On" : "Off"}
                </span>
                <input
                  type="checkbox"
                  role="switch"
                  checked={campaign.is_ready_to_publish}
                  disabled={busyLaunchId === campaign.launch_id}
                  onChange={() => toggleReady(campaign)}
                  aria-label={`Ready to publish ${campaign.launch_name}`}
                />
              </label>
              {campaign.history_count > 0 ? (
                <button
                  type="button"
                  className="cpc__history-link"
                  onClick={() =>
                    setHistoryFor(historyFor === campaign.launch_id ? null : campaign.launch_id)
                  }
                >
                  View history ({campaign.history_count})
                </button>
              ) : (
                <span className="cpc__history-none">No publish history</span>
              )}
            </div>

            {historyFor === campaign.launch_id && (
              <PublishHistory launchId={campaign.launch_id} />
            )}
          </div>
        );
      })}
    </div>
  );
};

/** The campaign's timeline: every publish attempt AND every readiness change, in time order.
    A failure that was later retried is what Ops is usually looking for, and "who turned this
    on" only makes sense read alongside "did it then go out". */
function PublishHistory({ launchId }: { launchId: number }) {
  const [entries, setEntries] = useState<HistoryEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    service
      .getPublishHistory(launchId)
      .then(setEntries)
      .catch((e) => setError(e instanceof Error ? e.message : "Could not load history"));
  }, [launchId]);

  if (error) return <div className="cpc__history cpc__error">{error}</div>;
  if (!entries) return <div className="cpc__history">Loading history…</div>;
  if (entries.length === 0) return <div className="cpc__history">Nothing recorded yet.</div>;

  return (
    <div className="cpc__history">
      {entries.map((entry, i) =>
        entry.kind === "readiness" ? (
          <div className="cpc__attempt" key={`r${i}`}>
            {/* Hollow marker: a toggle did not succeed or fail, it just happened. A green
                tick here would read as "this reached a network", which it did not. */}
            <span className="cpc__dot cpc__dot--note" title="Readiness change" />
            <span className="cpc__attempt-when">{formatDateTime(entry.at)}</span>
            <span className="cpc__attempt-who">{entry.user_name ?? "System"}</span>
            <span className="cpc__attempt-what">
              {entry.is_ready_to_publish ? "Marked ready" : "Marked not ready"}
            </span>
          </div>
        ) : (
          <div className="cpc__attempt" key={`p${entry.attempt_id ?? i}`}>
            <span
              className={`cpc__dot cpc__dot--${entry.is_successful ? "ok" : "fail"}`}
              title={entry.is_successful ? "Succeeded" : "Failed"}
            />
            <span className="cpc__attempt-when">{formatDateTime(entry.at)}</span>
            <span className="cpc__attempt-who">{entry.user_name ?? "System"}</span>
            <span className="cpc__attempt-nets">
              {(entry.networks ?? []).map((n) => (
                <span
                  key={`${entry.attempt_id}-${n.network}`}
                  className={`cpc__net cpc__net--${n.is_successful ? "ok" : "fail"}`}
                  title={n.error ?? undefined}
                >
                  {n.network}
                </span>
              ))}
            </span>
            {entry.published_paused && <span className="cpc__paused-badge">paused</span>}
          </div>
        )
      )}
    </div>
  );
}
